import cn from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ButtonV3 } from 'components/ComponentV2';
import Icon from 'components/Icon';
import Spinner from 'components/Spinner';
import { useNewsletters } from 'hooks/useNewsletters.hook';

import { withAuth } from 'lib/hocs/withAuth';
import { ImagesAttributes } from 'lib/models/newsletter';
import { PAGE_ROUTES } from 'lib/page-routes';
import Editor from '../Editor';

import styles from './Create.module.scss';

const CreateNewsletter = (): ReactElement => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSaved, setShowSaved] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>('');
  const [letter, setLetter] = useState<string>('');
  const [letterHtml, setLetterHtml] = useState<string>('');
  const [redirectHome, setRedirectHome] = useState<boolean>(false);

  const [newsletterImage, setNewsletterImage] = useState<
    { id: string; type: string; attributes: ImagesAttributes }[]
  >([]);

  const { updateNewsletter, getNewsletterData, createNewsletter, onDelete } =
    useNewsletters();
  const router = useRouter();
  const { id } = router.query as { id: string };
  const [newsletterId, setNewsletterId] = useState<string>(id);

  const fetchData = async () => {
    try {
      if (newsletterId) {
        const data = await getNewsletterData(newsletterId as string);
        setSubject(data.subject);
        setLetter(data.letter);
        setNewsletterImage(data.images.data);
        setLetterHtml(data.letter_html);
      }
    } catch (error) {
      console.error('Error during data fetch:', error);
    }
  };
  const handleEditorLoading = (loading: boolean, saved: boolean) => {
    setIsLoading(loading);
    setShowSaved(saved);
  };
  useEffect(() => {
    const { id, redirect } = router.query;
    if (redirect === 'home') {
      setRedirectHome(true);
    }
    setNewsletterId(id as string);
    if (id) {
      fetchData();
    }
  }, [router.query]);

  type SubjectChangeHandler = (
    value: string,
    id?: string,
    letter?: string
  ) => Promise<void>;

  function debounce(func: SubjectChangeHandler, delay: number) {
    let timeoutId: NodeJS.Timeout;

    return function (...args: Parameters<SubjectChangeHandler>) {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  const debouncedHandleSubjectChangeUpdate = useRef(
    debounce(async (value: string, letterHtml, id?: string) => {
      setIsLoading(true);
      if (id) {
        if (value === '' && !letterHtml) {
          await onDelete(id as string, false);
          router.push(`${PAGE_ROUTES.NEWSLETTER_CREATE}?redirect=home`);
        } else {
          await updateNewsletter({
            news_letter: {
              subject: value,
            },
            id,
          });
        }
      } else {
        const data = await createNewsletter({
          news_letter: { subject: value },
        });

        if (data) {
          router.push(
            `${PAGE_ROUTES.NEWSLETTER_CREATE}?id=${data.id}`,
            undefined,
            {
              shallow: true,
            }
          );
          setSubject(data.subject);
          setLetter(data.letter);
          setNewsletterImage(data.images.data);
          setNewsletterId(data.id);
        }
      }
      setTimeout(() => setIsLoading(false), 400);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1400);
    }, 500)
  ).current;
  const handleSubjectChange = async (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setSubject(event.target.value);

    await debouncedHandleSubjectChangeUpdate(
      event.target.value,
      letterHtml,
      newsletterId as string
    );
  };

  const css = useMemo(() => {
    return `
        div.ce-block__content,
        div.ce-toolbar__content {
          max-width: 816px;
        }
        div.ce-inline-toolbar {
          background-color: rgba(8, 12, 11, 0.97);
          color: rgba(244, 246, 248, 0.62);
          padding: 3px;
          border: none;
        }
        div.ce-inline-toolbar > button {
          padding: 6px;
        }
        .ce-inline-tool--active {
          color: rgba(244, 246, 248, 1);
        }
        @media (hover: hover) {
          .ce-inline-tool:hover {
            background-color: rgba(33, 34, 34, 1);
          }
        }
        .cdx-search-field.ce-popover__search,
        .cdx-input.image-tool__caption {
          display:none;
        }
        .buttonClass,
        .image-tool__image {
          margin: 10px 0;
        }
      `;
  }, []);

  const editor = useMemo(() => {
    if (!newsletterId)
      return (
        <div className={styles.disabledEditor}>
          Begin writing your newsletter...
        </div>
      );
    return (
      <Editor
        newsLetterResponse={{ id: newsletterId }}
        letter={letter}
        newsletterImage={newsletterImage || []}
        onUpdateLoading={handleEditorLoading}
        setEditorHtml={setLetterHtml}
      />
    );
  }, [newsletterId]);

  return (
    <>
      <Head>
        <title>Create newsletter - Huzzle</title>
        <meta name="description" content="Huzzle" />
        <style>{css}</style>
      </Head>
      <section className={styles.EditNewsletter} data-testid="CreateNewsletter">
        <div className={styles.header}>
          <div className={styles.wrapperContainer}>
            <div className={styles.wrapper}>
              <div
                onClick={() =>
                  redirectHome
                    ? router.push(PAGE_ROUTES.NEWSLETTERS)
                    : newsletterId
                    ? window.history.go(-2)
                    : router.back()
                }
              >
                <Icon className={cn(styles.closeIcon)} iconName="icon_close" />
              </div>

              <p className={styles.headerText}>New newsletter</p>
              {subject ? (
                isLoading ? (
                  <div className={styles.savingWrapper}>
                    <Spinner
                      color="black"
                      className={styles.spinner1}
                      size="medium"
                    />
                    <p className={styles.savingText}>Saving</p>
                  </div>
                ) : (
                  showSaved && (
                    <div className={styles.savingWrapper}>
                      <Icon iconName="icon_check-double" />
                      <p className={styles.savingText}>Saved</p>
                    </div>
                  )
                )
              ) : null}
            </div>

            {newsletterId && subject && letterHtml && (
              <div className={styles.preview}>
                <ButtonV3
                  size="small"
                  className={styles.previewButton}
                  onClick={() =>
                    router.push(`${PAGE_ROUTES.NEWSLETTER_PREVIEW}/${id}`)
                  }
                >
                  Preview
                </ButtonV3>
              </div>
            )}
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.contentWrapper}>
            <textarea
              value={subject}
              onFocus={fetchData}
              className={styles.heading}
              placeholder="Subject"
              onChange={handleSubjectChange}
            />
          </div>
        </div>
        {editor}
      </section>
    </>
  );
};
const CreateNewsletterWrapper = withAuth(CreateNewsletter);

export default CreateNewsletterWrapper;
